import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Cpu, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";

export default function AdminDevices() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tenantFilter = params.get("tenant") || "";
  const [tenants, setTenants] = useState([]);
  const [devices, setDevices] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => { api.get("/admin/tenants").then((r) => setTenants(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const url = tenantFilter ? `/admin/devices?tenant_id=${tenantFilter}` : "/admin/devices";
    api.get(url).then((r) => setDevices(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [tenantFilter]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const list = devices.filter((d) =>
    (status === "all" || d.status === status) &&
    `${d.client_name} ${d.serial} ${d.city} ${d.tenant_name}`.toLowerCase().includes(q.toLowerCase())
  );
  const online = devices.filter((d) => d.status === "active").length;

  return (
    <div className="h-full flex flex-col" data-testid="admin-devices-page">
      <div className="px-8 pt-8 pb-4 border-b border-kl-lineLight">
        <p className="kl-label">Platform</p>
        <h1 className="font-display font-light text-3xl mt-1">All devices <span className="font-mono text-sm text-kl-mutedLight">{devices.length}</span></h1>
        <div className="flex items-center gap-3 mt-4">
          <div className="relative flex-1 min-w-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-kl-mutedLight" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, serial, city, dealer" className="kl-input-box pl-9 h-9 w-full" data-testid="admin-devices-search" />
          </div>
          <select value={tenantFilter} onChange={(e) => setParams(e.target.value ? { tenant: e.target.value } : {})} className="kl-input-box h-9 w-44 shrink-0" data-testid="admin-devices-tenant-filter">
            <option value="">All dealers</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="kl-input-box h-9 w-36 shrink-0" data-testid="admin-devices-status-filter">
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <span className="font-mono text-xs text-kl-mutedLight shrink-0">{online} active</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto kl-scroll-light">
        <div className="grid grid-cols-[1.4fr_1.2fr_1fr_0.9fr_0.9fr_24px] gap-2 px-8 h-10 items-center kl-label border-b border-kl-lineLight sticky top-0 bg-white z-10">
          <span>Client</span><span>Serial</span><span>Dealer</span><span>Site</span><span className="text-right">Status · live</span><span />
        </div>
        {list.length === 0 && <p className="p-8 text-sm text-kl-mutedLight" data-testid="admin-devices-empty">No devices match.</p>}
        {list.map((d) => (
          <button
            key={d.id}
            onClick={() => nav(`/devices/${d.id}`)}
            className="w-full grid grid-cols-[1.4fr_1.2fr_1fr_0.9fr_0.9fr_24px] gap-2 px-8 h-14 items-center text-sm border-b border-kl-lineLight hover:bg-kl-light transition-colors text-left group"
            data-testid={`admin-device-row-${d.serial}`}
          >
            <span className="flex items-center gap-2 min-w-0"><Cpu size={14} className="text-kl-mutedLight shrink-0" /><span className="truncate">{d.client_name !== d.serial ? d.client_name : "Unassigned"}</span></span>
            <span className="font-mono text-xs truncate">{d.serial}</span>
            <span className="truncate text-kl-mutedLight">{d.tenant_name || "—"}</span>
            <span className="truncate text-kl-mutedLight">{[d.city, d.country].filter(Boolean).join(", ") || "—"}</span>
            <span className="text-right">
              <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest ${d.status === "active" ? "text-kl-greenDark" : "text-kl-inactive"}`}>
                <span className={`w-1.5 h-1.5 ${d.status === "active" ? "bg-kl-active" : "bg-kl-inactive"}`} />{d.status}
              </span>
              <span className="block text-[11px] text-kl-mutedLight font-mono">{d.live ? `${(d.live.power_w / 1000).toFixed(2)} kW · synced ${fmt.ago(d.last_seen)}` : d.last_seen ? `synced ${fmt.ago(d.last_seen)}` : "never synced"}</span>
            </span>
            <ChevronRight size={15} className="text-kl-mutedLight opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        ))}
      </div>
    </div>
  );
}
